import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import ImportMillon from "../components/team/ImportMillon";
import ImportChristies from "../components/team/ImportChristies";

export default function TeamAuctionHousesManager() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("auction_houses")
      .select("id,name,country,city,website,is_top_ten,top_ten_rank,active,updated_at")
      .order("is_top_ten", { ascending: false })
      .order("name", { ascending: true });
    if (!error) setRows(data || []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.city, row.country, row.website]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [q, rows]);

  function edit(id, key, value) {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  }

  async function save(id) {
    setSavingId(id);
    const row = rows.find((item) => item.id === id);
    const { error } = await supabase
      .from("auction_houses")
      .update({
        name: row.name,
        city: row.city || null,
        country: row.country || null,
        website: row.website || null,
        is_top_ten: !!row.is_top_ten,
        top_ten_rank: row.top_ten_rank ? Number(row.top_ten_rank) : null,
        active: !!row.active,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);
    setSavingId(null);
    if (!error) load();
  }

  async function create(e) {
    e.preventDefault();
    setCreating(true);
    const form = new FormData(e.currentTarget);
    const name = String(form.get("name") || "").trim();
    const country = String(form.get("country") || "").trim();
    const city = String(form.get("city") || "").trim();
    const website = String(form.get("website") || "").trim();
    const { error } = await supabase.rpc("fn_upsert_auction_house", {
      p_name: name,
      p_country: country,
      p_city: city,
      p_website: website,
    });
    setCreating(false);
    if (!error) {
      e.currentTarget.reset();
      load();
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{t("teamAuctionHouses.title")}</h1>
        <div className="flex gap-2">
          <input className="border rounded-md px-3 py-2 text-sm" placeholder={t("teamAuctionHouses.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
          <button onClick={load} className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm">{t("teamAuctionHouses.refresh")}</button>
        </div>
      </div>

      {/* Import d'une vente par URL — alimente la base des ventes/lots (comparables marché) */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-1">{t("teamAuctionHouses.importByUrlTitle", { defaultValue: "Importer une vente par URL" })}</h2>
        <p className="text-sm text-gray-600 mb-4">
          {t("teamAuctionHouses.importByUrlHelp", { defaultValue: "Colle l'URL d'une page de vente (Millon, Christie's). Les lots (artiste, titre, n°, estimation) sont scannés et ajoutés à la base des enchères générales." })}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ImportMillon />
          <ImportChristies />
        </div>
      </div>

      <h2 className="text-lg font-semibold mb-3">{t("teamAuctionHouses.housesSectionTitle", { defaultValue: "Maisons de vente référencées" })}</h2>
      <form onSubmit={create} className="border rounded-xl p-4 mb-8 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input name="name" className="border rounded px-3 py-2" placeholder={t("teamAuctionHouses.fields.nameRequired")} required />
        <input name="country" className="border rounded px-3 py-2" placeholder={t("teamAuctionHouses.fields.country")} />
        <input name="city" className="border rounded px-3 py-2" placeholder={t("teamAuctionHouses.fields.city")} />
        <input name="website" className="border rounded px-3 py-2 md:col-span-2" placeholder={t("teamAuctionHouses.fields.websitePlaceholder")} />
        <button disabled={creating} className="md:col-span-5 px-4 py-2 rounded bg-black text-white">
          {creating ? t("teamAuctionHouses.creating") : t("teamAuctionHouses.add")}
        </button>
      </form>

      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-600">
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.name")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.city")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.country")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.website")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.topTen")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.rank")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.active")}</th>
              <th className="py-2 px-3">{t("teamAuctionHouses.fields.action")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="py-6 px-3" colSpan={8}>{t("teamAuctionHouses.loading")}</td></tr>
            ) : filtered.length ? (
              filtered.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="py-2 px-3"><input className="border rounded px-2 py-1 w-full" value={row.name || ""} onChange={(e) => edit(row.id, "name", e.target.value)} /></td>
                  <td className="py-2 px-3"><input className="border rounded px-2 py-1 w-full" value={row.city || ""} onChange={(e) => edit(row.id, "city", e.target.value)} /></td>
                  <td className="py-2 px-3"><input className="border rounded px-2 py-1 w-full" value={row.country || ""} onChange={(e) => edit(row.id, "country", e.target.value)} /></td>
                  <td className="py-2 px-3"><input className="border rounded px-2 py-1 w-full" value={row.website || ""} onChange={(e) => edit(row.id, "website", e.target.value)} /></td>
                  <td className="py-2 px-3"><input type="checkbox" checked={!!row.is_top_ten} onChange={(e) => edit(row.id, "is_top_ten", e.target.checked)} /></td>
                  <td className="py-2 px-3"><input className="border rounded px-2 py-1 w-20" type="number" min="1" value={row.top_ten_rank || ""} onChange={(e) => edit(row.id, "top_ten_rank", e.target.value)} /></td>
                  <td className="py-2 px-3"><input type="checkbox" checked={!!row.active} onChange={(e) => edit(row.id, "active", e.target.checked)} /></td>
                  <td className="py-2 px-3">
                    <button onClick={() => save(row.id)} className="px-3 py-1 rounded bg-black text-white">
                      {savingId === row.id ? t("teamAuctionHouses.saving") : t("teamAuctionHouses.save")}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td className="py-6 px-3" colSpan={8}>{t("teamAuctionHouses.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
